import { action, makeObservable, observable, runInAction } from 'mobx';
import { destroy, get, getCount, patch, post, buildWhereFilter } from 'stores/_services/dataService';
import { getConditions } from 'stores/_services/filterService';
import { SortDirection } from 'constants/SortDirection';
import { ILocation } from 'stores/location/LocationStore';
import { IDataFetchOptions } from '../../types';

export interface IOrganization {
    id?: string | null;
    name?: string;
    description?: string;
    tenantId?: string;
    locationCount?: number;
    agentCount?: number;
    alertCount?: number;
    data?: {
        vsaId?: string;
    };
}

export default class OrganizationStore {
    organization: IOrganization = {
        id: '',
        name: '',
        description: ''
    };

    locations: ILocation[] = [];
    locationSearch = '';
    locationSort = {
        by: 'name',
        dir: SortDirection.ASC
    };
    locationCount = 0;
    devices = [];
    deviceCount = 0;
    policies = [];
    policySort = {
        by: 'policyType',
        dir: SortDirection.ASC
    };

    constructor(organization: IOrganization) {
        makeObservable(this, {
            organization: observable,
            locations: observable,
            locationSearch: observable,
            locationSort: observable,
            locationCount: observable,
            devices: observable,
            deviceCount: observable,
            policies: observable,
            policySort: observable,
            update: action,
            clearDetails: action,
            getOrganizationLocations: action,
            editOrganization: action,
            deleteOrganization: action
        });

        this.update(organization);
    }

    update(organization: IOrganization): void {
        this.organization = {
            ...this.organization,
            ...organization
        };
    }

    clearDetails(): void {
        this.policies = [];
        this.locations = [];
        this.devices = [];
        this.locationSearch = '';
        this.locationSort = {
            by: 'name',
            dir: SortDirection.ASC
        };
    }

    async getLocationCount(): Promise<void> {
        const params = {
            where: { organizationId: this.organization.id }
        };
        const count = await getCount('locationDetails', params);
        runInAction(() => {
            this.locationCount = count;
        });
    }

    async getDeviceCount(): Promise<void> {
        const params = {
            where: { organizationId: this.organization.id }
        };
        const count = await getCount('agentDetails', params);
        runInAction(() => {
            this.deviceCount = count;
        });
    }

    async getCounts(): Promise<void> {
        await this.getLocationCount();
        await this.getDeviceCount();
    }

    async getOrganizationLocations(name?: string, sortBy?: string, sortDirection?: SortDirection, id?: string): Promise<void> {
        const conditions = getConditions({
            organization: {
                id: id ? id : this.organization.id
            },
            search: name ?? this.locationSearch
        });

        const { data } = await get('locationDetails', {
            filter: {
                searchFields: ['name'],
                where: conditions,
                include: {
                    relation: 'organization',
                    scope: {
                        fields: ['name', 'data']
                    }
                },
                order: `${sortBy ?? this.locationSort.by} ${sortDirection ?? this.locationSort.dir}`
            }
        });
        runInAction(() => {
            this.locations = data;
            if (sortBy) {
                this.locationSort.by = sortBy;
            }
            if (sortDirection) {
                this.locationSort.dir = sortDirection;
            }
            this.locationSearch = name ?? this.locationSearch;
        });
        await this.getCounts();
    }

    async getOrganizationDevices(): Promise<void> {
        const { data } = await get('agentDetails', {
            filter: {
                where: { organizationId: this.organization.id },
                include: {
                    relation: 'location',
                    scope: {
                        fields: ['name', 'data']
                    }
                }
            }
        });
        runInAction(() => {
            this.devices = data.map((item: any) => ({
                ...item,
                locationName: item.location.name
            }));
        });
    }

    async getOrganizationPolicies(args: IDataFetchOptions = {}, conditions?: object): Promise<void> {
        const options: any = {
            filter: {
                searchFields: ['policyName', 'policyDescription'],
                order: `${args.sortBy ?? this.policySort.by} ${args.sortDirection ?? this.policySort.dir}`,
                where: conditions
            }
        };

        const { data } = await get(`organizations/${this.organization.id}/policyAssignments`, options);

        runInAction(() => {
            this.policies = data;
            if (args.sortBy) {
                this.policySort.by = args.sortBy;
            }
            if (args.sortDirection) {
                this.policySort.dir = args.sortDirection;
            }
        });
    }

    async existsOrganizationName(name: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name }
            }
        };
        const { data } = await get('organizations', options);
        return !!data.length;
    }

    async existsLocationName(name: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, organizationId: this.organization.id }
            }
        };
        const { data } = await get('locations', options);
        return !!data.length;
    }

    async existsLocationNameInOrganization(name: string, organizationId: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, organizationId: organizationId }
            }
        };
        const { data } = await get('locations', options);
        return !!data.length;
    }

    async editOrganization(name: string, description: string): Promise<boolean> {
        const exists = name !== this.organization.name && (await this.existsOrganizationName(name));
        if (exists) {
            return false;
        }
        const payload = {
            name: name,
            description: description
        };
        await patch(`organizations/${this.organization.id}`, payload);
        runInAction(() => {
            this.organization.name = name;
            this.organization.description = description;
        });
        return true;
    }

    async deleteOrganization(): Promise<void> {
        await destroy(`organizations/${this.organization.id}`);
    }

    async editLocation(id: string, name: string, description: string, oldName: string): Promise<boolean> {
        const exists = name !== oldName && (await this.existsLocationName(name));
        if (exists) {
            return false;
        }
        const payload = {
            ...(name !== oldName ? { name: name } : {}),
            description: description
        };
        await patch(`locations/${id}`, payload);
        await this.getOrganizationLocations();
        return true;
    }

    async deleteLocation(id: string): Promise<void> {
        const payload = {
            idsList: [id]
        };

        await post('locations/deleteLocations', payload);

        await this.getOrganizationLocations();
    }

    async moveLocation({
                           id,
                           organizationId,
                           name
                       }: {
        id: string,
        organizationId: string,
        name: string,
    }): Promise<boolean> {
        const exists = await this.existsLocationNameInOrganization(name, organizationId);
        const newName = exists ? `${name}_` : name;

        const payload = {
            organizationId,
            name: newName,
            idsList: [id]
        };

        await post('locations/moveLocations', payload);

        await this.getOrganizationLocations();
        return !exists;
    }

    async assignPolicy(policyId: string, deviceGroupId: string): Promise<void> {
        const payload = {
            organizationId: this.organization.id,
            deviceGroupId,
            filter: {
                where: { id: policyId }
            }
        };

        await post(`policies/assignToOrganization`, payload);
        await this.getOrganizationPolicies();
    }

    async unassignPolicy(policyAssignmentId: string): Promise<void> {
        const payload = [policyAssignmentId];
        await post('policies/removePolicyAssignments', payload);
        await this.getOrganizationPolicies();
    }

    async enablePolicy(policyId: string, policyName: string, deviceGroupId: string): Promise<void> {
        const payload = {
            where: { id: policyId },
            name: this.organization.name,
            policyName,
            deviceGroupId
        };
        await post(`policies/enableForOrganization/${this.organization.id}`, payload);
        await this.getOrganizationPolicies();
    }

    async disablePolicy(policyId: string, policyName: string, deviceGroupId: string): Promise<void> {
        const payload = {
            where: { id: policyId },
            name: this.organization.name,
            policyName,
            deviceGroupId
        };
        await post(`policies/disableForOrganization/${this.organization.id}`, payload);
        await this.getOrganizationPolicies();
    }
}
